import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  ErrorHandler,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  signal,
  InjectionToken,
} from '@angular/core';
import type ApexCharts from 'apexcharts';
import type { ApexOptions } from './apex-chart.types';

export type ApexChartsLoader = () => Promise<typeof ApexCharts>;

export const APEX_CHARTS_LOADER = new InjectionToken<ApexChartsLoader>(
  'ApexCharts engine loader',
  {
    providedIn: 'root',
    factory: () => () => import('apexcharts').then(module => module.default),
  }
);

const OPTION_KEYS = [
  'series',
  'chart',
  'xaxis',
  'yaxis',
  'colors',
  'tooltip',
  'legend',
  'fill',
  'stroke',
  'dataLabels',
  'grid',
  'plotOptions',
  'title',
  'labels',
  'states',
  'theme',
  'responsive',
] as const satisfies readonly (keyof ApexOptions)[];

// Keep the existing template API while exposing only the engine operations used by this app.
@Component({
  selector: 'apx-chart',
  template: '<div #chartHost></div>',
  standalone: true,
})
export class ApexChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() series?: ApexOptions['series'];
  @Input() chart?: ApexOptions['chart'];
  @Input() xaxis?: ApexOptions['xaxis'];
  @Input() yaxis?: ApexOptions['yaxis'];
  @Input() colors?: ApexOptions['colors'];
  @Input() tooltip?: ApexOptions['tooltip'];
  @Input() legend?: ApexOptions['legend'];
  @Input() fill?: ApexOptions['fill'];
  @Input() stroke?: ApexOptions['stroke'];
  @Input() dataLabels?: ApexOptions['dataLabels'];
  @Input() grid?: ApexOptions['grid'];
  @Input() plotOptions?: ApexOptions['plotOptions'];
  @Input() title?: ApexOptions['title'];
  @Input() labels?: ApexOptions['labels'];
  @Input() states?: ApexOptions['states'];
  @Input() theme?: ApexOptions['theme'];
  @Input() responsive?: ApexOptions['responsive'];
  @ViewChild('chartHost', { static: true })
  private chartHost!: ElementRef<HTMLElement>;

  private readonly instance = signal<ApexCharts | null>(null);
  readonly chartInstance = this.instance.asReadonly();
  private readonly browser: boolean;
  private readonly pending = new Set<string>();
  private viewReady = false;
  private destroyed = false;
  private scheduled = false;
  private flushing = false;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private retryDelay = 1000;

  constructor(
    private readonly zone: NgZone,
    private readonly errorHandler: ErrorHandler,
    @Inject(PLATFORM_ID) platformId: Object,
    @Inject(APEX_CHARTS_LOADER) private readonly loadEngine: ApexChartsLoader
  ) {
    this.browser = isPlatformBrowser(platformId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
    Object.keys(changes).forEach(key => this.pending.add(key));
    this.schedule();
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.pending.add('initial');
    this.schedule();
  }

  private schedule(): void {
    if (
      !this.browser ||
      !this.viewReady ||
      this.destroyed ||
      this.scheduled ||
      this.flushing ||
      this.retryTimer !== undefined
    )
      return;
    this.scheduled = true;
    this.zone.runOutsideAngular(() => {
      void Promise.resolve().then(async () => {
        this.scheduled = false;
        if (this.destroyed) return;
        try {
          await this.flush();
        } catch (error) {
          if (!this.destroyed) this.errorHandler.handleError(error);
        }
      });
    });
  }

  private async flush(): Promise<void> {
    this.flushing = true;
    try {
      while (!this.destroyed && this.pending.size) {
        const seriesOnly =
          this.pending.size === 1 && this.pending.has('series');
        this.pending.clear();
        const current = this.chartInstance();
        if (seriesOnly && current) {
          try {
            await this.zone.runOutsideAngular(() =>
              current.updateSeries(this.series ?? [], true)
            );
            this.retryDelay = 1000;
            continue;
          } catch (error) {
            if (this.destroyed) return;
            this.errorHandler.handleError(error);
          }
        }
        await this.rebuild();
        this.retryDelay = 1000;
      }
    } catch (error) {
      if (!this.destroyed) {
        // Rebuild from the latest inputs, including changes received during loading/rendering.
        this.pending.add('rebuild');
        this.zone.runOutsideAngular(() => {
          this.retryTimer = setTimeout(() => {
            this.retryTimer = undefined;
            this.schedule();
          }, this.retryDelay);
        });
        this.retryDelay = Math.min(this.retryDelay * 2, 30000);
      }
      throw error;
    } finally {
      this.flushing = false;
      if (this.pending.size && !this.destroyed) this.schedule();
    }
  }

  private async rebuild(): Promise<void> {
    // Keep the engine lazy and never mount after destruction.
    const ApexChartsConstructor = await this.loadEngine();
    if (this.destroyed) return;
    const options: ApexOptions = {};
    for (const key of OPTION_KEYS) {
      const value = this[key];
      if (value !== undefined && value !== null) {
        (options as Record<string, unknown>)[key] = value;
      }
    }
    let instance: ApexCharts;
    if (options.chart?.events) {
      const component = this;
      const events = { ...options.chart.events };
      for (const key of Object.keys(events)) {
        const callback = events[key];
        if (typeof callback !== 'function') continue;
        events[key] = function (...args: unknown[]) {
          if (!component.destroyed && component.chartInstance() === instance) {
            return callback.apply(this, args);
          }
        };
      }
      options.chart = { ...options.chart, events };
    }
    // Read all latest inputs after loading; changes during the import are included.
    this.pending.clear();
    this.destroyChart();
    instance = this.zone.runOutsideAngular(
      () => new ApexChartsConstructor(this.chartHost.nativeElement, options)
    );
    this.instance.set(instance);
    try {
      await this.zone.runOutsideAngular(() => instance.render());
    } catch (error) {
      if (!this.destroyed && this.chartInstance() === instance) {
        this.destroyChart();
        throw error;
      }
    }
  }

  // The donut's label click uses this method. AI Monitoring calls the engine
  // instance received through chart.events.mounted; those callbacks pass through.
  updateOptions(
    options: ApexOptions,
    redrawPaths?: boolean,
    animate?: boolean,
    updateSyncedCharts?: boolean
  ): Promise<void> {
    const current = this.chartInstance();
    if (!current || this.destroyed) return Promise.resolve();
    return this.zone.runOutsideAngular(() =>
      current.updateOptions(options, redrawPaths, animate, updateSyncedCharts)
    );
  }

  private destroyChart(): void {
    const current = this.chartInstance();
    this.instance.set(null);
    if (current) this.zone.runOutsideAngular(() => current.destroy());
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
    this.pending.clear();
    this.destroyChart();
  }
}
